/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { AsyncResult } from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { Collapse, Drawer, Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState } from 'react';
import Logo from './components/logo.component';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
// import SearchView from '../../../modules/moe-search/moe-search.view';
// import MoeButton from '../../../modules/moe-button/moe-button.view';
// import { BsFillArrowRightCircleFill, BsChevronDown } from 'react-icons/bs';
import { getAsset } from '@msdyn365-commerce/core';
import { readAsync, updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { IS_LOCAL_DEVELOPMENT, getOnlineChannelInfoTimer, setOnlineChannelInfoInCache } from '../../../utilities/language-change';
import { getMOEOnlineStoresAsync } from '../../../actions/extensions/DataActionExtension.g';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    onTelemetryClick,
    TelemetryConstant,
    getTelemetryObject
} from '@msdyn365-commerce-modules/utilities';
import { AXE_MAORI_LANG_CODE } from '../../../utilities/constants';
import MsDyn365 from '@msdyn365-commerce/core';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = (props: IHeaderViewProps & IHeaderProps<{}>) => {
    const { HeaderTag, HeaderContainer, HeaderTopBarContainer, context } = props;
    const { logoLink, logoImage, tewharakiRedirectURL, kauwhatareoRedirectURL, headerText, supportBtnUrl, headerTextLink } = props.config;

    // State variables
    // const [switchCurriculumUrl, setSwitchCurriculumUrl] = useState<any>('select');
    // const [switchCurriculumName, setSwitchCurriculumName] = useState<any>('Switch curriculum');
    // const [toggleCurriculumState, setToggleCurriculumState] = useState<any>(false);

    // const changeSwitchCurriculum = (curriculum: any) => {
    //     if (curriculum === 'twk') {
    //         setSwitchCurriculumUrl(tewharakiRedirectURL);
    //         setSwitchCurriculumName('Te Whariki');
    //         setToggleCurriculumState(!toggleCurriculumState);
    //     } else if (curriculum === 'kwr') {
    //         setSwitchCurriculumUrl(kauwhatareoRedirectURL);
    //         setSwitchCurriculumName('Kauwhata reo');
    //         setToggleCurriculumState(!toggleCurriculumState);
    //     }
    // };

    // const toggleCurriculum = () => {
    //     setToggleCurriculumState(!toggleCurriculumState);
    // };

    const hostname = context.request.url?.requestUrl?.hostname;

    React.useEffect(() => {
        if (IS_LOCAL_DEVELOPMENT) {
            return;
        }

        if (MsDyn365.isBrowser && !localStorage.getItem(`moeOnlineChannelInfo${hostname}`)) {
            void getMOEOnlineStoresAsync({ callerContext: props.context.actionContext, bypassCache: 'get' }).then(channelResponse => {
                setOnlineChannelInfoInCache(context, JSON.stringify(channelResponse));
            });
        } else {
            const hours = 24;
            const setupTime = getOnlineChannelInfoTimer(context);
            const now = new Date().getTime();

            if (MsDyn365.isBrowser && now - setupTime > hours * 60 * 60 * 1000) {
                localStorage.removeItem('moeOnlineChannelInfoTimer');
                void getMOEOnlineStoresAsync({ callerContext: props.context.actionContext, bypassCache: 'get' }).then(channelResponse => {
                    setOnlineChannelInfoInCache(context, JSON.stringify(channelResponse));
                });
            }
        }
    }, []);

    // bug 102348: Effect for changing document lang code to Maori
    React.useEffect(() => {
        const htmlElement: HTMLElement | null = document.querySelector('html');
        if (htmlElement) {
            htmlElement.lang = AXE_MAORI_LANG_CODE;
            htmlElement.translate = false;
        }
    }, []);

    function _renderCollapseMenu(props: IHeaderViewProps): JSX.Element | null {
        const { Divider, MobileMenuLinksContainer, mobileMenuCollapsed } = props;
        // const deleteLaterVariable = _renderMobileAccountBlock(props, true);
        // console.log(deleteLaterVariable);
        return (
            <Collapse className='ms-header__collapsible-hamburger' isOpen={!mobileMenuCollapsed}>
                {_renderReactFragment(props.menuBar)}
                <Node {...Divider} />
                <Node {...MobileMenuLinksContainer}>
                    {_renderReactFragment(props.search)}
                    <div className='moe-site-controls'>
                        <div className='moe-site-controls--left'>
                            {_renderMobileAccountBlock(props, true)}
                            <div className='moe-support-btn'>
                                <a className='msc-btn moe-support-btn--link' role='button' href={supportBtnUrl}>
                                    <i className='far fa-question-circle'></i>
                                    <text>Support</text>
                                </a>
                            </div>
                        </div>

                        <div className='moe-site-controls--right'>
                            <span className='moe-site-controls__lbl'>Switch Curriculum:</span>
                            <div className='moe-switchsite'>
                                <a className='moe-switchsite__link' href={tewharakiRedirectURL} target='_blank'>
                                    <img
                                        className='moe-switchsite__img twk-logo'
                                        title='Te Whāriki Online'
                                        src={getAsset('images/twk-thumbnail.jpg', props.context.request)}
                                        alt='Te Whāriki Online logo'
                                    />
                                </a>
                                <a className='moe-switchsite__link' href={kauwhatareoRedirectURL} target='_blank'>
                                    <img
                                        className='moe-switchsite__img kwr-logo'
                                        title='Kauwhata Reo'
                                        src={getAsset('images/kwr-thumbnail.png', props.context.request)}
                                        alt='Kauwhata Reo logo'
                                    />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* <div className='moe-form__control moe-form__control--switchcurriculum' aria-label='Switch curriculum form'>
                        <div className='moe-select'>
                            <button onClick={toggleCurriculum} className='moe-select__button'>
                                {switchCurriculumName}
                                <span className='moe-select__chevrondown'>
                                    <BsChevronDown />
                                </span>
                            </button>
                            <a
                                className='moe-form__control-gobtn'
                                href={switchCurriculumUrl}
                                style={{ pointerEvents: switchCurriculumUrl === 'select' ? 'none' : 'auto' }}
                            >
                                <span className='sr-only'>Go</span>
                                <BsFillArrowRightCircleFill />
                            </a>
                        </div>
                        <ul className={`moe-select__list ${toggleCurriculumState === false ? 'hide' : 'show'}`}>
                            <li className='moe-select__item'>
                                <button onClick={() => changeSwitchCurriculum('twk')}>Te Whāriki</button>
                            </li>
                            <li className='moe-select__item'>
                                <button onClick={() => changeSwitchCurriculum('kwr')}>Kauwhata Reo</button>
                            </li>
                        </ul>
                    </div> */}
                </Node>
            </Collapse>
        );
    }

    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    <div className='moe-header__desktop moe-header__desktop--nzc'>
                        <div className='moe-header__desktop-container'>
                            <div className='moe-header__desktop-container-left'>
                                <Logo
                                    {...{
                                        link: logoLink,
                                        image: logoImage,
                                        className: 'ms-header__logo moe-header__logo',
                                        gridSettings: props.context.request.gridSettings,
                                        requestContext: props.context.request,
                                        typeName: props.typeName
                                    }}
                                />
                                <div className='moe-header__logo-txt-column'>
                                    {headerText?.trim() === 'The Hub' ? (
                                        <h2 className='moe-header__logo-txt'>{headerText}</h2>
                                    ) : (
                                        <a href={headerTextLink?.linkUrl.destinationUrl} className='moe-header__logo-txt'>
                                            {headerText}
                                        </a>
                                    )}
                                    {/* <div
                                        className='moe-form__control moe-form__control--switchcurriculum'
                                        aria-label='Switch curriculum form'
                                    >
                                        <div className='moe-select'>
                                            <button onClick={toggleCurriculum} className='moe-select__button'>
                                                {switchCurriculumName}
                                                <span className='moe-select__chevrondown'>
                                                    <BsChevronDown />
                                                </span>
                                            </button>
                                            <a
                                                className='moe-form__control-gobtn'
                                                href={switchCurriculumUrl}
                                                style={{ pointerEvents: switchCurriculumUrl === 'select' ? 'none' : 'auto' }}
                                            >
                                                <span className='sr-only'>Go</span>
                                                <BsFillArrowRightCircleFill />
                                            </a>
                                        </div>
                                        <ul className={`moe-select__list ${toggleCurriculumState === false ? 'hide' : 'show'}`}>
                                            <li className='moe-select__item'>
                                                <button onClick={() => changeSwitchCurriculum('twk')}>Te Whāriki</button>
                                            </li>
                                            <li className='moe-select__item'>
                                                <button onClick={() => changeSwitchCurriculum('kwr')}>Kauwhata Reo</button>
                                            </li>
                                        </ul>
                                    </div> */}
                                </div>
                            </div>
                            <div className='moe-header__desktop-container-right'>
                                <div className='moe-search-wrap'>{_renderReactFragment(props.search)}</div>
                                <div className='moe-header-actions'>
                                    {_renderAccountBlock(props, false)}
                                    {props.cartIcon}
                                    <a className='moe-btn moe-btn--primary-blue' role='button' href={supportBtnUrl}>
                                        <text>Support</text>
                                    </a>
                                    {_renderReactFragment(props.siteOptions)}
                                    {props.navIcon}
                                </div>
                            </div>
                            {props.preferredStore}
                        </div>
                        <Node className='moe-nav-wrap nzc-nav-wrap'>
                            {_renderReactFragment(props.menuBar)}
                            <div className='moe-switchsite'>
                                <a className='moe-switchsite__link' href={tewharakiRedirectURL} target='_blank'>
                                    <img
                                        className='moe-switchsite__img twk-logo'
                                        title='Te Whāriki Online'
                                        src={getAsset('images/twk-thumbnail.jpg', props.context.request)}
                                        alt='Te Whāriki Online logo'
                                    />
                                </a>
                                <a className='moe-switchsite__link' href={kauwhatareoRedirectURL} target='_blank'>
                                    <img
                                        className='moe-switchsite__img kwr-logo'
                                        title='Kauwhata Reo'
                                        src={getAsset('images/kwr-thumbnail.png', props.context.request)}
                                        alt='Kauwhata Reo logo'
                                    />
                                </a>
                            </div>
                        </Node>
                    </div>
                    <div className='moe-header__mobile moe-header__mobile--nzc'>
                        <div className='moe-header__mobile-container'>
                            <div className='moe-header__mobile-container-left'>
                                <Logo
                                    {...{
                                        link: logoLink,
                                        image: logoImage,
                                        className: 'ms-header__logo moe-header__logo',
                                        gridSettings: props.context.request.gridSettings,
                                        requestContext: props.context.request,
                                        typeName: props.typeName
                                    }}
                                />
                                <a href={headerTextLink?.linkUrl.destinationUrl} className='moe-header__logo-txt'>
                                    {headerText}
                                </a>
                            </div>
                            <div className='moe-header__mobile-container-right'>
                                <div className='moe-header-actions'>
                                    {_renderAccountBlock(props, false)}
                                    {props.cartIcon}
                                    {props.navIcon}
                                </div>
                            </div>
                        </div>
                    </div>
                </Node>
                {_renderCollapseMenu(props)}
            </Node>
        </Module>
    );
};

const renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
    const customer = accountInformation && accountInformation.result;
    return <>{customer ? customer.FirstName || customer.Name : ''}</>;
};
// const deleteAllCookies = () => {
//     const cookies = document.cookie.split(';');

//     for (let i = 0; i < cookies.length; i++) {
//         const cookie = cookies[i];
//         const eqPos = cookie.indexOf('=');
//         const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
//         document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
//     }
// };
function _renderMobileAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const { AccountInfoDropdownParentContainer, signInLink, data, accountLinks } = props;
    const signOutLinkInEnglish = _renderSignoutLinkInEnglish(props);

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            'ms-header__drawer',
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );

        if (accountLinks) {
            return (
                <Drawer
                    className={accountClassName}
                    openGlyph='ms-header__drawer-open'
                    closeGlyph='ms-header__drawer-close'
                    glyphPlacement='end'
                    toggleButtonText={renderCustomerName(data.accountInformation)}
                >
                    <div>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLinkInEnglish}
                    </div>
                </Drawer>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }
    return null;
}

function _renderAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signInLink,
        accountLinks
    } = props;

    const signOutLinkInEnglish = _renderSignoutLinkInEnglish(props);

    if (AccountInfoDropdownParentContainer) {
        const accountClassName = classnames(
            AccountInfoDropdownParentContainer.className,
            renderForMobile ? 'account-mobile' : 'account-desktop'
        );
        if (AccountInfoDropdownPopoverConentContainer) {
            _updateUserDetails(props);
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                        {signOutLinkInEnglish}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                    {signInLink}
                </Node>
            );
        }
    }

    return null;
}

function _updateUserDetails(props: any) {
    const { context } = props;
    const [isUpdatedUserDetails, setIsUpdatedUserDetails] = useState(false);

    if (context.request.user?.isAuthenticated) {
        if (!isUpdatedUserDetails) {
            try {
                readAsync({ callerContext: context.actionContext }, context.request.user.customerAccountNumber).then(customerData => {
                    if (
                        customerData.FirstName === context.request.user.firstName &&
                        customerData.LastName === context.request.user.lastName &&
                        customerData.Email === context.request.user.emailAddress
                    ) {
                        setIsUpdatedUserDetails(true);
                        return;
                    }
                    customerData.FirstName = context.request.user.firstName;
                    customerData.LastName = context.request.user.lastName;
                    customerData.Name = context.request.user.name;
                    customerData.Email = context.request.user.emailAddress;

                    const entity = { ...customerData, Attributes: customerData.Attributes };
                    updateAsync({ callerContext: context.actionContext, bypassCache: 'get' }, entity).then(updatedCustomerData => {
                        if (updatedCustomerData) {
                            setIsUpdatedUserDetails(true);
                        }
                    });
                });
            } catch (err) {
                console.error('Get all the customers data err', err);
            }
        }
    }
}

function _renderSignoutLinkInEnglish(props: IHeaderViewProps & IHeaderProps<{}>): React.ReactNode | undefined {
    const {
        data: { accountInformation },
        context: {
            request: { user }
        },
        resources
    } = props;
    const customer = accountInformation && accountInformation.result;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const displayName = customer ? customer.FirstName || customer.Name : undefined;

    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignOut);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);

    const deleteAllCookies = () => {
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }

        onTelemetryClick(telemetryContent, payLoad, 'Sign out');
        location.href = user.signOutUrl as any;
    };
    return (
        customer &&
        displayName && (
            <Button
                className='ms-header__signout-button'
                title={resources.signOutLinkText}
                href={(user && user.signOutUrl) || ''}
                aria-label={resources.signOutLinkText}
                onClick={deleteAllCookies}
                {...attributes}
            >
                <span className='ms-header__signout-button-text' aria-hidden>
                    {resources.signOutLinkText}
                </span>
            </Button>
        )
    );
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

export default headerView;
